'use client';

import React, { useState, useMemo } from 'react';
import { Egg, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Plus, Download, FileText, Filter, Target, Package, BarChart2, List, Layers, Activity, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar
} from 'recharts';
import {
  MOCK_EGG_PRODUCTION_RECORDS,
  MOCK_TREND_DATA,
  MOCK_FLOCK_SUMMARIES,
  MOCK_INSIGHTS,
  FlockProductionSummary,
  EggProductionInsight,
} from './eggProductionData';
import Icon from '@/components/ui/AppIcon';
import { useRole } from '@/lib/useRole';


// ─── KPI Card ─────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: string | number;
  sub?: string;
  change?: number;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  href?: string;
}

function KpiCard({ label, value, sub, change, icon: Icon, iconBg, iconColor, href }: KpiCardProps) {
  const content = (
    <div className="rounded-xl border border-border/60 bg-card p-4 flex items-start gap-3 shadow-sm hover:shadow-md transition-all duration-200 h-full">
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        <Icon size={15} className={iconColor} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-muted-foreground leading-tight truncate">{label}</p>
        <p className="text-xl font-semibold tracking-tight mt-0.5 font-tabular leading-tight text-foreground">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{sub}</p>}
        {change !== undefined && (
          <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${change >= 0 ? 'text-green-600' : 'text-red-500'}`}>
            {change >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
            {change >= 0 ? '+' : ''}{change}% vs yesterday
          </div>
        )}
      </div>
    </div>
  );
  return href ? <Link href={href} className="block h-full">{content}</Link> : content;
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function ProductionStatusBadge({ status }: { status: FlockProductionSummary['status'] }) {
  const map = {
    excellent: 'bg-green-100 text-green-700',
    good: 'bg-blue-100 text-blue-700',
    below_target: 'bg-amber-100 text-amber-700',
    critical: 'bg-red-100 text-red-700',
  };
  const labels = { excellent: 'Excellent', good: 'Good', below_target: 'Below Target', critical: 'Critical' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${map[status]}`}>
      {labels[status]}
    </span>
  );
}

// ─── Insight Card ─────────────────────────────────────────────────────────────

function InsightCard({ insight }: { insight: EggProductionInsight }) {
  const map = {
    warning: { bg: 'bg-amber-50 border-amber-200', icon: AlertTriangle, iconColor: 'text-amber-500', titleColor: 'text-amber-800' },
    critical: { bg: 'bg-red-50 border-red-200', icon: AlertTriangle, iconColor: 'text-red-500', titleColor: 'text-red-800' },
    info: { bg: 'bg-blue-50 border-blue-200', icon: Activity, iconColor: 'text-blue-500', titleColor: 'text-blue-800' },
    success: { bg: 'bg-green-50 border-green-200', icon: CheckCircle, iconColor: 'text-green-500', titleColor: 'text-green-800' },
  };
  const cfg = map[insight.type];
  const Icon = cfg.icon;
  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl border ${cfg.bg}`}>
      <Icon size={16} className={`${cfg.iconColor} flex-shrink-0 mt-0.5`} />
      <div>
        <p className={`text-xs font-bold ${cfg.titleColor}`}>{insight.title}</p>
        <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{insight.message}</p>
      </div>
    </div>
  );
}

// ─── Chart Filters ────────────────────────────────────────────────────────────

const CHART_PERIODS = ['7 Days', '30 Days', 'This Month', 'Previous Month'];
const CHART_SERIES = [
  { key: 'totalEggs', label: 'Total Eggs', color: '#16a34a' },
  { key: 'goodEggs', label: 'Good Eggs', color: '#22c55e' },
  { key: 'rejectedEggs', label: 'Rejected', color: '#ef4444' },
  { key: 'crackedEggs', label: 'Cracked', color: '#f97316' },
  { key: 'dirtyEggs', label: 'Dirty', color: '#eab308' },
  { key: 'hatchingEggs', label: 'Hatching', color: '#8b5cf6' },
];

// ─── Main Content ─────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart2 },
  { id: 'flocks', label: 'By Flock', icon: Layers },
  { id: 'records', label: 'Recent Records', icon: List },
];

export default function EggProductionContent() {
  const [activeTab, setActiveTab] = useState('overview');
  const [chartPeriod, setChartPeriod] = useState('7 Days');
  const [activeSeries, setActiveSeries] = useState<string[]>(['totalEggs', 'goodEggs', 'rejectedEggs']);
  const [showFilters, setShowFilters] = useState(false);
  const { can } = useRole();

  const todayRecords = useMemo(() =>
    MOCK_EGG_PRODUCTION_RECORDS.filter(r => r.productionDate === '2026-09-04'),
    []
  );

  const eggsToday = todayRecords.reduce((s, r) => s + r.totalEggs, 0);
  const goodEggsToday = todayRecords.reduce((s, r) => s + r.goodEggs, 0);
  const crackedToday = todayRecords.reduce((s, r) => s + r.crackedEggs, 0);
  const dirtyToday = todayRecords.reduce((s, r) => s + r.dirtyEggs, 0);
  const rejectedToday = todayRecords.reduce((s, r) => s + r.rejectedEggs, 0);
  const hatchingToday = todayRecords.reduce((s, r) => s + r.hatchingEggs, 0);
  const traysToday = todayRecords.reduce((s, r) => s + r.fullTrays, 0);
  const avgHDP = todayRecords.length > 0
    ? (todayRecords.reduce((s, r) => s + r.henDayProduction, 0) / todayRecords.length).toFixed(1)
    : '0';

  const eggsThisWeek = MOCK_TREND_DATA.reduce((s, d) => s + d.totalEggs, 0);
  const eggsThisMonth = eggsThisWeek * 4;

  function toggleSeries(key: string) {
    setActiveSeries(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  }

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Egg Production</h1>
          <p className="text-sm text-gray-500 mt-1">
            Monitor daily egg production, quality, grading, stock and performance across your poultry farm.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowFilters(f => !f)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
          >
            <Filter size={15} />
            Filters
          </button>
          {can('egg_production.export') && (
            <button className="flex items-center gap-2 px-3 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors">
              <Download size={15} />
              Export
            </button>
          )}
          {can('egg_reports.view') && (
            <button className="flex items-center gap-2 px-3 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors">
              <FileText size={15} />
              Report
            </button>
          )}
          {can('egg_production.create') && (
            <Link
              href="/egg-production/records/new"
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-sm"
            >
              <Plus size={15} />
              Record Production
            </Link>
          )}
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 mb-6 shadow-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {['Date Range', 'Farm', 'Branch', 'Poultry House', 'Flock', 'Breed', 'Status'].map(f => (
              <select key={f} className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-green-500">
                <option>{f}</option>
              </select>
            ))}
          </div>
        </div>
      )}

      {/* Module Quick Nav */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { label: 'Records', href: '/egg-production/records', permission: 'egg_production.view' as const },
          { label: 'Grading', href: '/egg-production/grading', permission: 'egg_grading.manage' as const },
          { label: 'Inventory', href: '/egg-production/inventory', permission: 'egg_inventory.view' as const },
          { label: 'Transfers', href: '/egg-production/transfers', permission: 'egg_transfers.manage' as const },
          { label: 'Order Fulfillment', href: '/egg-production/order-fulfillment', permission: 'egg_inventory.view' as const },
          { label: 'Analytics', href: '/egg-production/analytics', permission: 'egg_production.view' as const },
          { label: 'Reports', href: '/egg-production/reports', permission: 'egg_reports.view' as const },
          { label: 'Settings', href: '/egg-production/settings', permission: 'egg_settings.manage' as const },
        ].filter(item => can(item.permission)).map(item => (
          <Link
            key={item.href}
            href={item.href}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-colors shadow-sm"
          >
            {item.label}
            <ArrowRight size={11} />
          </Link>
        ))}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-6">
        <KpiCard label="Eggs Today" value={eggsToday.toLocaleString()} change={8.5} icon={Egg} iconBg="bg-green-100" iconColor="text-green-600" href="/egg-production/records" />
        <KpiCard label="Eggs This Week" value={eggsThisWeek.toLocaleString()} sub="7-day total" icon={TrendingUp} iconBg="bg-blue-100" iconColor="text-blue-600" />
        <KpiCard label="Good Eggs" value={goodEggsToday.toLocaleString()} sub={`${((goodEggsToday / eggsToday) * 100).toFixed(1)}% of total`} icon={CheckCircle} iconBg="bg-emerald-100" iconColor="text-emerald-600" />
        <KpiCard label="Rejected Eggs" value={rejectedToday} sub="Today" change={-2.1} icon={AlertTriangle} iconBg="bg-red-100" iconColor="text-red-500" />
        <KpiCard label="Cracked Eggs" value={crackedToday} sub="Today" icon={AlertTriangle} iconBg="bg-orange-100" iconColor="text-orange-500" />
        <KpiCard label="Dirty Eggs" value={dirtyToday} sub="Today" icon={AlertTriangle} iconBg="bg-yellow-100" iconColor="text-yellow-600" />
        <KpiCard label="Hatching Eggs" value={hatchingToday.toLocaleString()} sub="Breeder flock" icon={Egg} iconBg="bg-purple-100" iconColor="text-purple-600" />
        <KpiCard label="Total Trays" value={traysToday} sub="Today (30-egg trays)" icon={Package} iconBg="bg-teal-100" iconColor="text-teal-600" />
        <KpiCard label="Hen-Day %" value={`${avgHDP}%`} sub="Farm average today" icon={Target} iconBg="bg-indigo-100" iconColor="text-indigo-600" />
        <KpiCard label="Eggs This Month" value={(eggsThisMonth).toLocaleString()} sub="Estimated" icon={BarChart2} iconBg="bg-cyan-100" iconColor="text-cyan-600" />
        <KpiCard label="Avg Eggs/Bird" value="0.88" sub="Today" icon={Activity} iconBg="bg-pink-100" iconColor="text-pink-600" />
        <KpiCard label="Current Stock" value="15,470" sub="Eggs in store" icon={Package} iconBg="bg-gray-100" iconColor="text-gray-600" href="/egg-production/inventory" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
        {TABS.map(tab => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
                isActive ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <TabIcon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab: Overview */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Production Trend Chart */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <h2 className="text-base font-bold text-gray-900">Egg Production Trend</h2>
                <p className="text-xs text-gray-400 mt-0.5">Daily production across all flocks</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {CHART_PERIODS.map(p => (
                  <button
                    key={p}
                    onClick={() => setChartPeriod(p)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                      chartPeriod === p ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            {/* Series toggles */}
            <div className="flex flex-wrap gap-2 mb-4">
              {CHART_SERIES.map(s => (
                <button
                  key={s.key}
                  onClick={() => toggleSeries(s.key)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                    activeSeries.includes(s.key)
                      ? 'border-transparent text-white' :'border-gray-200 text-gray-400 bg-white'
                  }`}
                  style={activeSeries.includes(s.key) ? { backgroundColor: s.color } : {}}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                  {s.label}
                </button>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={MOCK_TREND_DATA} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                />
                {CHART_SERIES.filter(s => activeSeries.includes(s.key)).map(s => (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    stroke={s.color}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name={s.label}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Insights + Alerts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h2 className="text-base font-bold text-gray-900 mb-4">Intelligent Insights</h2>
              <div className="space-y-3">
                {MOCK_INSIGHTS.map(insight => (
                  <InsightCard key={insight.id} insight={insight} />
                ))}
              </div>
            </div>

            {/* Production by House Bar Chart */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h2 className="text-base font-bold text-gray-900 mb-1">Production by Flock</h2>
              <p className="text-xs text-gray-400 mb-4">Today's eggs per flock</p>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={MOCK_FLOCK_SUMMARIES.map(f => ({ name: f.flockNumber, eggs: f.eggsToday, target: Math.round(f.currentBirds * f.targetPct / 100) }))}
                  margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="eggs" name="Actual" fill="#16a34a" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="target" name="Target" fill="#bbf7d0" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Tab: By Flock */}
      {activeTab === 'flocks' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-base font-bold text-gray-900">Egg Production by Flock</h2>
            <span className="text-xs text-gray-400">{MOCK_FLOCK_SUMMARIES.length} active flocks</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Flock', 'Breed', 'Age (wk)', 'Birds', 'Eggs Today', 'This Week', 'HDP %', 'Target %', 'Variance', 'Status'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MOCK_FLOCK_SUMMARIES.map((flock, i) => (
                  <tr key={flock.flockId} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900 text-xs">{flock.flockNumber}</div>
                      <div className="text-xs text-gray-400 truncate max-w-[140px]">{flock.house}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{flock.breed}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{flock.ageWeeks}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{flock.currentBirds.toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs font-semibold text-gray-900">{flock.eggsToday.toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{flock.eggsThisWeek.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold ${flock.henDayPct >= flock.targetPct ? 'text-green-600' : 'text-amber-600'}`}>
                        {flock.henDayPct}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{flock.targetPct}%</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold ${flock.variance >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {flock.variance >= 0 ? '+' : ''}{flock.variance}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <ProductionStatusBadge status={flock.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab: Recent Records */}
      {activeTab === 'records' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-base font-bold text-gray-900">Recent Production Records</h2>
            <Link href="/egg-production/records" className="text-xs text-green-600 font-semibold hover:underline">
              View All Records →
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Date', 'Record #', 'House', 'Flock', 'Total Eggs', 'Good', 'Cracked', 'Rejected', 'HDP %', 'Trays', 'Status'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MOCK_EGG_PRODUCTION_RECORDS.slice(0, 6).map((rec, i) => (
                  <tr key={rec.id} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{rec.productionDate}</td>
                    <td className="px-4 py-3">
                      <Link href={`/egg-production/records/${rec.id}`} className="text-xs font-semibold text-green-700 hover:underline">{rec.recordNumber}</Link>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{rec.house}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{rec.flockNumber}</td>
                    <td className="px-4 py-3 text-xs font-semibold text-gray-900">{rec.totalEggs.toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs text-green-600 font-medium">{rec.goodEggs.toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs text-orange-500">{rec.crackedEggs}</td>
                    <td className="px-4 py-3 text-xs text-red-500">{rec.rejectedEggs}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold ${rec.henDayProduction >= (rec.targetProduction ?? 0) ? 'text-green-600' : 'text-amber-600'}`}>
                        {rec.henDayProduction}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{rec.fullTrays}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                        rec.status === 'approved' ? 'bg-green-100 text-green-700' :
                        rec.status === 'submitted'? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {rec.status.charAt(0).toUpperCase() + rec.status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
